<template>
    <div v-if="content" class="flex">
        <div v-if="showImage" :class="imageWrapperClass">
            <img v-if="getSmallImage"
                 v-on-error
                 :srcset="`${getSmallImage} 1x, ${getLargeImage} 2x`"
                 :alt="content.displayName"
                 loading="lazy"
                 class="rounded-full employee-image">
            <div v-else
                 class="rounded-full employee-image image-placeholder flex items-center justify-center max-w-full"
                 :style="missingImageStyle">
                <div class="text-white oprema:text-black text-24 font-semi-bold">
                    {{ content.initials }}
                </div>
            </div>
        </div>
        <div class="pr-10 text-13" :class="{ 'pl-20 w-2/3': showImage }">
            <div class="text-15 font-semi-bold" :class="nameLineClass">
                {{ content.displayName }}
            </div>
            <div :class="titleLineClass">
                {{ content.title }}
            </div>
            <div v-if="content.directPhone && !isBot" :class="textLineClass">
                {{ $dictionary.get('Generic.Phone.DirectPhone.ShortLabel') }} <a :href="`tel:${content.directPhone}`" class="" target="_blank">{{ content.directPhone }}</a>
            </div>
            <div v-if="content.mobilePhone && !isBot" :class="textLineClass">
                {{ $dictionary.get('Generic.Phone.MobilePhone.ShortLabel') }} <a :href="`tel:${content.mobilePhone}`" class="" target="_blank">{{ content.mobilePhone }}</a>
            </div>
            <div v-if="showEmail" class="break-all" :class="textLineClass">
                <a :href="`mailto:${content.email}`" class="theme-text-linkcolor" target="_blank">{{ content.email }}</a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { StyleValue, PropType, computed } from 'vue';
import serverContextService from '@/core/serverContext.service';
import { EmployeeViewObject } from '@/types/apiServerContract';
import responsiveProductImageService from '@/project/product/image/responsiveProductImage.service';
import botService from '@/project/site/bot.service';

const props = defineProps({
    content: { type: Object as PropType<Partial<EmployeeViewObject>>, required: true },
    index: { type: Number, default: 0 },
    showImagePlaceholderIfEmpty: { type: Boolean, default: true },
    textLineClass: { type: String, default: 'mb-5' },
    nameLineClass: { type: String, default: 'mb-5' },
    titleLineClass: { type: String, default: 'mb-5' },
    imageWrapperClass: { type: String, default: 'w-1/3' },
});

const missingImageStyle = computed((): StyleValue => {
    const colors: string[] = serverContextService.isOprema ? ['#fffef2', '#abd3df', '#ededed'] :
        serverContextService.isHesa ? ['#BFD1D1', '#FFDBD8', '#DCDCDC'] :
            ['#c5d2e0', '#c9c0b7', '#9dbcb0'];
    const styles: StyleValue = {
        backgroundColor: colors[props.index % colors.length]
    };
    return styles;
});

const getLargeImage = computed((): string | null => {
    return props.content && props.content.photoUrl ? responsiveProductImageService.formatImageUrl(props.content.photoUrl, '256') : null;
});

const getSmallImage = computed((): string | null => {
    return props.content && props.content.photoUrl ? responsiveProductImageService.formatImageUrl(props.content.photoUrl, '128') : null;
});

const showEmail = computed((): boolean => {
    return !!(props.content.email && !isBot.value);
});

const isBot = computed((): boolean => {
    return botService.uaIsBot() || botService.uaIsPrerender();
});

const showImage = computed(():boolean => {
    return !!(props.showImagePlaceholderIfEmpty || getSmallImage.value);
});

</script>
<style lang="less" scoped>
.employee-image {
    width:12.8rem;
    aspect-ratio: 1;
}
</style>
